// The ONE place that knows about build-time connection defaults.
//
// Every account API client used to carry its own
// `const DEFAULT_API_BASE = "https://api.ato.run"` and its own
// `import.meta.env.VITE_ACCOUNT_API_BASE_URL ?? DEFAULT_API_BASE` expression —
// 40 files, 45 sites, several spellings, and inconsistent trailing-slash
// handling. That is 40 independent answers to "what am I connected to", which
// cannot survive a second realization. This module is the only reader of
// `import.meta.env` for connection configuration.

import type { Capability, ConnectionProfile, InjectedConnection } from "./types";
import { DEFAULT_ACCOUNT_API_BASE, normalizeAccountApiBase } from "./hostedEndpoint";
export { DEFAULT_ACCOUNT_API_BASE } from "./hostedEndpoint";

/**
 * Hosted capabilities: everything. Declared for completeness only — no
 * component reads `capabilities` until S7.
 */
const HOSTED_CAPABILITIES: Capability[] = [
  "public_feed",
  "search",
  "oauth",
  "managed_compute",
  "billing",
  "local_library",
  "activity",
  "agent_controller",
  "participation",
  "browser_adapter",
];

/** Strip a trailing slash so callers can always append "/v1/...". */
function normalizeEndpoint(raw: string): string {
  return raw.replace(/\/$/, "");
}

/** `https://x` → `wss://x`; `http://x` → `ws://x`. */
function realtimeFromHttp(httpEndpoint: string): string {
  return httpEndpoint.replace(/^http/, "ws");
}

/**
 * Resolve the hosted profile from build-time configuration.
 *
 * Read lazily, on every call, rather than captured at module load. Two reasons,
 * both real: the existing suite drives configuration with `vi.stubEnv` after
 * modules are imported, and a future local profile is injected by the Desktop
 * shell at runtime rather than baked in. Caching would break the first and
 * quietly pin the second to whatever was true at import time.
 */
function resolveHostedProfile(): ConnectionProfile {
  // Resolve the fallback here so Vite can remove the production default from
  // a staging bundle; a fallback inside the helper would retain both origins.
  const httpEndpoint = normalizeAccountApiBase(
    import.meta.env.VITE_ACCOUNT_API_BASE_URL ?? DEFAULT_ACCOUNT_API_BASE,
  );
  return {
    kind: "hosted",
    httpEndpoint,
    realtimeEndpoint: realtimeFromHttp(httpEndpoint),
    // Hosted auth is the cookie session on api.ato.run. Clients still set
    // `credentials: "include"` directly; making them derive it from this field
    // is S3.
    auth: { kind: "cookie" },
    capabilities: HOSTED_CAPABILITIES,
  };
}

/**
 * The global the Desktop shell injects before the app boots.
 *
 * A global rather than a fetch on purpose: the app must know what it is
 * connected to before it issues its first request, and there is no endpoint to
 * ask until the shell has told it one.
 */
const INJECTED_KEY = "__ATO_CONNECTION__";

declare global {
  interface Window {
    __ATO_CONNECTION__?: unknown;
  }
}

/**
 * Read the shell-injected connection, if this build is running inside Desktop.
 *
 * Validated rather than trusted: a malformed injection must fall back to the
 * hosted resolution instead of producing a half-built profile that fails later
 * at an arbitrary call site.
 */
function readInjectedConnection(): InjectedConnection | null {
  if (typeof window === "undefined") return null;
  const raw = window[INJECTED_KEY] as Partial<InjectedConnection> | undefined;
  if (!raw || typeof raw !== "object") return null;
  if (raw.kind !== "local") return null;
  const endpoint = raw.httpEndpoint;
  if (endpoint !== null && typeof endpoint !== "string") return null;
  return {
    kind: "local",
    httpEndpoint: endpoint ?? null,
    auth: raw.auth,
    capabilities: raw.capabilities,
  };
}

/**
 * Build the local profile from what the shell injected.
 *
 * `httpEndpoint: null` is a legitimate, expected state — the Coordinator has
 * not bound a port yet, or failed to start. It resolves to an empty endpoint so
 * that requests fail visibly and locally instead of silently escaping to the
 * hosted API, which is the one outcome a local realization must never produce.
 */
function resolveLocalProfile(injected: InjectedConnection): ConnectionProfile {
  const httpEndpoint = injected.httpEndpoint
    ? normalizeEndpoint(injected.httpEndpoint)
    : "";
  return {
    kind: "local",
    httpEndpoint,
    realtimeEndpoint: httpEndpoint ? realtimeFromHttp(httpEndpoint) : "",
    // Until the shell injects a credential (S6), a local connection is
    // unauthenticated rather than falsely cookie-authenticated.
    auth: injected.auth ?? { kind: "cookie" },
    capabilities: injected.capabilities ?? [],
  };
}

/** Whether the Coordinator has given the app a usable endpoint yet. */
export function isConnectionAvailable(): boolean {
  return connectionProfile().httpEndpoint !== "";
}

/**
 * The active connection profile.
 *
 * Hosted unless the Desktop shell injected a local connection. Either way it is
 * resolved HERE and nowhere else, so no caller learns which realization it is
 * running against.
 */
export function connectionProfile(): ConnectionProfile {
  const injected = readInjectedConnection();
  return injected ? resolveLocalProfile(injected) : resolveHostedProfile();
}

/**
 * The account API base — the single most-used field of the profile, and the
 * direct replacement for every former `import.meta.env.VITE_ACCOUNT_API_BASE_URL
 * ?? DEFAULT_API_BASE` expression.
 */
export function accountApiBase(): string {
  return connectionProfile().httpEndpoint;
}
